/* eslint-disable @typescript-eslint/no-explicit-any */
import { CellClickedEvent, ColDef, GridOptions } from '@ag-grid-community/core'
import { Component, Input, OnDestroy } from '@angular/core'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosDefaultColDef,
} from '@helios/ag-grid-config'
import { DensityLevel, UserPreferencesService } from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { AccountsDialogComponent } from '../accounts-dialog/accounts-dialog.component'
import {
  AccountSummary,
  AccountSummaryModel,
  accountSummaryModelFieldMap,
  AccountSummaryModelPropertyField,
  AccountSummaryModelValueField,
} from '../models/account-summary'
import { corporateActionDetailsModelField } from '../models/corporate-actions-details'
import { CorporateActionsService } from '../services/corporate-actions.service'

@Component({
  selector: 'hs-account-summary',
  templateUrl: './account-summary.component.html',
})
export class AccountSummaryComponent implements OnDestroy {
  _currentCorporateActionId: string | null = ''
  accountSummary: AccountSummary | undefined
  accountSummaryRowData: AccountSummaryModel[] = []
  gridOptionsaccountSummary: GridOptions =
    this.createAccountSummaryGridOptions()
  issueAccountMap: Map<string, string> | undefined
  accountSummaryLoading = false

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    public corporateActionsService: CorporateActionsService,
    private readonly modalService: NgbModal,
    private readonly preferences: UserPreferencesService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  createAccountSummaryGridOptions(): GridOptions {
    return {
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: 200,
        sortable: true,
      },
      columnDefs: this.accountSummaryColDefs(),
      onCellClicked: event => this.onCellClicked(event),
      sideBar: false,
      onGridReady: params => {
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
        enableHeliosCopyBehavior(params.api)
      },
    }
  }

  onCellClicked(event: CellClickedEvent) {
    const accountIds = event.data.value.join(',')
    if (accountIds && accountIds.length > 0) {
      const modalRef = this.modalService.open(AccountsDialogComponent, {
        size: 'md',
        windowClass: 'modal-window-click-through',
        scrollable: true,
        keyboard: false,
      } as NgbModalOptions)
      modalRef.componentInstance.accountIds = accountIds
    }
  }

  accountSummaryColDefs(): ColDef[] {
    return [
      {
        field: corporateActionDetailsModelField('property'),
        headerName: 'Property',
        cellStyle: params => {
          if (
            params.data.property !=
              AccountSummaryModelPropertyField.ASSOCIATED &&
            params.data.property != AccountSummaryModelPropertyField.WITHISSUES
          ) {
            return { 'margin-left': '15px' }
          }
          return null
        },
      },
      {
        field: corporateActionDetailsModelField('value'),
        headerName: 'Account IDs',
      },
    ]
  }

  @Input() set currentCorporateActionId(value: string | null) {
    if (this._currentCorporateActionId != value) {
      this._currentCorporateActionId = value
      this.getAccountSummary()
    }
  }

  getAccountSummary() {
    if (this._currentCorporateActionId) {
      this.accountSummaryLoading = true
      this.corporateActionsService
        .searchAccountsAffected(this._currentCorporateActionId)
        .subscribe(accountSummary => {
          this.accountSummary = accountSummary
          this.accountSummaryRowData = []
          if (this.accountSummary) {
            this.updateAccountSummaryData(
              new Map(Object.entries(accountSummary)),
            )
          }
          this.accountSummaryLoading = false
        })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateAccountSummaryData(accountSummaryMap: Map<string, any>) {
    accountSummaryModelFieldMap.forEach((value, key) => {
      if (accountSummaryMap.get(key)) {
        if (
          key == AccountSummaryModelValueField.ASSOCIATEDACCOUNTS ||
          key == AccountSummaryModelValueField.ALLISSUEACCOUNTS
        ) {
          this.accountSummaryRowData.push({
            property: value,
            value:
              accountSummaryMap.get(key).length != 0
                ? accountSummaryMap.get(key)
                : 'N/A',
          })
        }
        if (key == AccountSummaryModelValueField.ISSUESTATUSACCOUNTS) {
          this.issueAccountMap = new Map(
            Object.entries(
              accountSummaryMap.get(
                AccountSummaryModelValueField.ISSUESTATUSACCOUNTS,
              ),
            ),
          )
          this.issueAccountMap.forEach((accountWithIssues, issueName) => {
            issueName =
              issueName.charAt(0).toUpperCase() +
              issueName.toLowerCase().slice(1)
            this.accountSummaryRowData.push({
              property: issueName,
              value: accountWithIssues.length == 0 ? 'N/A' : accountWithIssues,
            })
          })
        }
      }
    })
  }
}
